/**
 * PermissionService — Frontend RBAC (Story 21.1)
 *
 * Единственный источник правды для прав доступа на frontend.
 * Синхронизирован с backend: app/auth/permissions.py (ROLE_PERMISSIONS матрица).
 *
 * При изменении матрицы прав — обновлять оба файла!
 * TODO Story 21.3: генерировать из единого JSON источника автоматически.
 *
 * Архитектура:
 * - Читает cabinet_role из Keycloak JWT claim 'cabinet_roles' (формат: "cabinet_id:role")
 * - Применяет ROLE_PERMISSIONS матрицу (идентична backend)
 * - Поддерживает дальнейшую эволюцию к ABAC (scope_restrictions, Story 21.2)
 */

import { Injectable, computed, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { CabinetStateService } from './cabinet-state.service';
import { OrgStateService } from './org-state.service';

// ---------------------------------------------------------------------------
// Types — синхронизировать с app/auth/permissions.py
// ---------------------------------------------------------------------------

export type CabinetRole =
  | 'owner'
  | 'admin'
  | 'manager'
  | 'member'
  | 'viewer'
  | 'demo_viewer';

export type Permission =
  | 'team.view'
  | 'team.invite'
  | 'team.manage'
  | 'settings.view'
  | 'settings.edit'
  | 'api_keys.view'
  | 'api_keys.manage'
  | 'catalog.view'
  | 'catalog.edit'
  | 'orders.view'
  | 'prices.view'
  | 'prices.edit'
  | 'repricing.view'
  | 'repricing.manage'
  | 'price_comparison.view'
  | 'warehouse.view'
  | 'warehouse.manage'
  | 'discount.view'
  | 'discount.manage'
  | 'analytics.view'
  | 'incidents.view'
  | 'incidents.manage'
  | 'cabinet.delete'
  | 'cabinet.manage'
  | 'org_finance.view'
  | 'org_finance.manage';

// ---------------------------------------------------------------------------
// Permission Matrix — синхронизировать с app/auth/permissions.py
// ---------------------------------------------------------------------------

const ROLE_PERMISSIONS: Record<CabinetRole, Permission[]> = {
  owner: [
    'team.view', 'team.invite', 'team.manage',
    'settings.view', 'settings.edit',
    'api_keys.view', 'api_keys.manage',
    'catalog.view', 'catalog.edit',
    'orders.view',
    'prices.view', 'prices.edit',
    'repricing.view', 'repricing.manage',
    'price_comparison.view',
    'warehouse.view', 'warehouse.manage',
    'analytics.view',
    'incidents.view', 'incidents.manage',
    'cabinet.delete', 'cabinet.manage',
    'org_finance.view',
    'org_finance.manage',
  ],
  admin: [
    'team.view', 'team.invite', 'team.manage',
    'settings.view', 'settings.edit',
    'api_keys.view', 'api_keys.manage',
    'catalog.view', 'catalog.edit',
    'orders.view',
    'prices.view', 'prices.edit',
    'repricing.view', 'repricing.manage',
    'price_comparison.view',
    'warehouse.view', 'warehouse.manage',
    'analytics.view',
    'incidents.view', 'incidents.manage',
    'org_finance.view',
    'org_finance.manage',
  ],
  manager: [
    'team.view', 'team.invite',
    'api_keys.view',           // Story 21.3: MANAGER видит маскированные ключи (нужно для работы с МП)
    'catalog.view', 'catalog.edit',
    'orders.view',
    'prices.view', 'prices.edit',
    'repricing.view', 'repricing.manage',
    'price_comparison.view',
    'warehouse.view', 'warehouse.manage',
    'analytics.view',
    'incidents.view',
  ],
  member: [
    'catalog.view', 'catalog.edit',
    'orders.view',
    'prices.view', 'prices.edit',
    'repricing.view', 'repricing.manage',
    'price_comparison.view',
    'warehouse.view', 'warehouse.manage',
    'analytics.view',
    'incidents.view',
  ],
  viewer: [
    'catalog.view',
    'orders.view',
    'prices.view',
    'repricing.view',
    'price_comparison.view',
    'warehouse.view',
    'analytics.view',
    'incidents.view',
  ],
  // Demo-mode: ALL view permissions (see every section), ZERO write/manage.
  demo_viewer: [
    'team.view',
    'settings.view',
    'api_keys.view',
    'catalog.view',
    'orders.view',
    'prices.view',
    'repricing.view',
    'price_comparison.view',
    'warehouse.view',
    'discount.view',
    'analytics.view',
    'incidents.view',
    // Story 99.24 AC-10 (2026-07-07): demo видит ВСЕ разделы, включая
    // орг-финансы — без этого PermissionGuard редиректит
    // /organizational-finance/* на dashboard. Write-защита — D2 read-only
    // гейт + отсутствие org_finance.manage в этом списке.
    'org_finance.view',
  ],
};

// Story 121.9D: active org OWNER получает ОБА finance-права (view+manage)
// через свою org-роль, даже если cabinet-роль (MEMBER/VIEWER/etc) их не даёт.
// Source-derived зеркало backend ORG_ROLE_PERMISSIONS[OrgRole.OWNER] ∩ finance
// (parity-тест test_org_finance_frontend_parity.py).
const ORG_OWNER_FINANCE: Permission[] = ['org_finance.view', 'org_finance.manage'];

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private authService = inject(AuthService);
  private cabinetState = inject(CabinetStateService);
  private orgState = inject(OrgStateService);
  /**
   * KeycloakService инжектируется в constructor (не в методах) —
   * это единственный корректный injection context для Angular 16+.
   * null если Keycloak не используется (useKeycloakAuth=false).
   */
  private _keycloak: KeycloakService | null = null;

  constructor() {
    if (environment.useKeycloakAuth) {
      try {
        this._keycloak = inject(KeycloakService);
      } catch {
        // KeycloakService не зарегистрирован в текущем контексте (тесты, non-KC mode)
        this._keycloak = null;
      }
    }
  }

  /**
   * Текущая cabinet_role пользователя.
   *
   * Источники (в порядке приоритета):
   * 1. CabinetStateService.activeCabinet().role — роль из БД (cabinet_membership),
   *    обновляется при переключении кабинета (Story 21.9)
   * 2. Keycloak JWT claim 'cabinet_roles' (формат: "cabinet_id:role")
   *    Пример: ["550e8400-e29b-41d4-a716-446655440000:admin"]
   * 3. Fallback через mapUserRole(user.role) если KC claim отсутствует
   * 4. 'viewer' если ничего не найдено (минимальные права)
   */
  private _currentRole = computed<CabinetRole>(() => {
    const user = this.authService.currentUser();
    if (!user) return 'viewer';

    // Приоритет 1: роль из БД через CabinetStateService (Story 21.9)
    // Это самый надёжный источник — приходит от GET /user/cabinets
    const activeCabinet = this.cabinetState.activeCabinet();
    if (activeCabinet?.role) {
      return this._normalizeCabinetRole(activeCabinet.role);
    }

    // Приоритет 2: Keycloak JWT claim 'cabinet_roles'
    if (environment.useKeycloakAuth && this._keycloak) {
      const activeId = this.cabinetState.activeCabinetId() || user.cabinetId;
      const role = this._getRoleFromKeycloakToken(activeId);
      if (role) return role;
    }

    // Fallback: маппинг из legacy UserRole
    return this._mapLegacyRole(user.role as string);
  });

  /**
   * Set прав текущей роли (computed, пересчитывается при смене роли).
   * Story 21.2 T7: Set<Permission> для O(1) lookup вместо Array.includes() O(n).
   */
  private _permissions = computed<Set<Permission>>(() => {
    return new Set(ROLE_PERMISSIONS[this._currentRole()] ?? []);
  });

  // ─── Public API ─────────────────────────────────────────────────────────────

  /** Текущая роль пользователя в активном кабинете. */
  role(): CabinetRole {
    return this._currentRole();
  }

  /** Проверить одно право. */
  can(permission: Permission): boolean {
    if (this._permissions().has(permission)) return true;
    // Story 112.6 -> 121.9D: active org OWNER получает ОБА finance-права
    // (view+manage) через свою org-роль, даже если cabinet-роль
    // (MEMBER/VIEWER/etc) их не даёт. Узкая проверка через source-derived
    // ORG_OWNER_FINANCE — не общая org-permission система.
    if (ORG_OWNER_FINANCE.includes(permission) && this.orgState.activeOrg()?.is_owner) {
      return true;
    }
    return false;
  }

  /** Проверить наличие любого из перечисленных прав (OR). */
  canAny(...permissions: Permission[]): boolean {
    return permissions.some(p => this.can(p));
  }

  /** Проверить наличие всех перечисленных прав (AND). */
  canAll(...permissions: Permission[]): boolean {
    return permissions.every(p => this.can(p));
  }

  // ─── Private Helpers ────────────────────────────────────────────────────────

  /**
   * Извлечь cabinet_role из Keycloak JWT claim 'cabinet_roles'.
   * Формат: ["cabinet_id:role", ...]
   * Пример: ["550e8400-e29b-41d4-a716-446655440000:admin"]
   */
  private _getRoleFromKeycloakToken(cabinetId: string): CabinetRole | null {
    const tokenParsed = this._keycloak?.getKeycloakInstance()?.tokenParsed;
    if (!tokenParsed) return null;

    const cabinetRoles: string[] = (tokenParsed as Record<string, unknown>)['cabinet_roles'] as string[] || [];

    for (const entry of cabinetRoles) {
      const [id, role] = entry.split(':');
      if (id === cabinetId && role) {
        return this._normalizeCabinetRole(role);
      }
    }

    // Нет роли для текущего кабинета — проверить cabinet_role (single value)
    const singleRole = (tokenParsed as Record<string, unknown>)['cabinet_role'] as string | undefined;
    if (singleRole) {
      return this._normalizeCabinetRole(singleRole);
    }

    return null;
  }

  /** Нормализовать строку роли в CabinetRole. */
  private _normalizeCabinetRole(role: string): CabinetRole {
    // 99.24: 'demo_viewer' исторически отсутствовал в valid и молча деградировал
    // в 'viewer' — латентно, пока наборы прав viewer/demo_viewer совпадали по
    // видимости разделов; org_finance.view (112.6 + demo-грант 99.24) создал
    // первое расхождение и баг проявился (PermissionGuard резал демо-раздел).
    const valid: CabinetRole[] = ['owner', 'admin', 'manager', 'member', 'viewer', 'demo_viewer'];
    const normalized = role.toLowerCase() as CabinetRole;
    return valid.includes(normalized) ? normalized : 'viewer';
  }

  /** Маппинг legacy UserRole → CabinetRole (fallback). */
  private _mapLegacyRole(legacyRole: string): CabinetRole {
    const mapping: Record<string, CabinetRole> = {
      'owner': 'owner',
      'admin': 'admin',
      'manager': 'manager',
      'operator': 'member',
      'member': 'member',
      'viewer': 'viewer',
    };
    return mapping[legacyRole?.toLowerCase()] ?? 'viewer';
  }
}
